import { useState, useEffect } from 'react';

/**
 * Window size interface
 */
interface WindowSize {
  width: number;
  height: number;
}

export type WindowCheckSizeType = {
  windowSize: WindowSize;
  isMobileView: boolean;
  isIpadProView: boolean;
  isIpadView: boolean;
  isMobileHorizontalView: boolean;
  isMobileMode: boolean;
  isMediumDesktop: boolean;
};

/**
 * Hook to track window size
 * @returns Current window width and height
 */
export default function useWindowSize(): WindowCheckSizeType {
  // Initialize with undefined to handle SSR
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures effect runs only on mount and unmount

  const isIpadProView = windowSize
    ? 992 < windowSize.width && windowSize.width < 1366
    : false;
  const isIpadView = windowSize
    ? 667 < windowSize.width && windowSize.width < 993
    : false;
  const isMobileHorizontalView = windowSize
    ? 575 < windowSize.width && windowSize.width < 768
    : false;
  const isMobileView = windowSize ? windowSize.width < 576 : false;
  const isMobileMode = isMobileView || isMobileHorizontalView;

  const isMediumDesktop = windowSize
    ? 992 < windowSize.width && windowSize.width <= 1280
    : false;

  return {
    windowSize,
    isMobileView,
    isIpadProView,
    isIpadView,
    isMobileHorizontalView,
    isMobileMode,
    isMediumDesktop,
  };
}
